/**
 * Partner Vehicles Tab — CRUD vehicles + document upload + driver assignment
 */
import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import {
  Car, Plus, Edit2, Trash2, Loader2, Upload, CheckCircle, Clock, FileText, X, User, UserX
} from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const STATUS_BADGE = {
  PENDING_VERIFICATION: { label: 'En attente', cls: 'bg-amber-500/20 text-amber-400' },
  APPROVED: { label: 'Approuvé', cls: 'bg-green-500/20 text-green-400' },
  REJECTED: { label: 'Rejeté', cls: 'bg-red-500/20 text-red-400' },
};
const VDOC_LABELS = {
  carte_grise: 'Carte grise', assurance: 'Assurance', controle_technique: 'Contrôle technique',
  macaron_vtc: 'Macaron VTC',
  photo_interieur_1: 'Photo intérieur 1', photo_interieur_2: 'Photo intérieur 2',
  photo_exterieur_1: 'Photo extérieur 1', photo_exterieur_2: 'Photo extérieur 2',
};
const VDOC_TYPES = [
  'carte_grise', 'assurance', 'controle_technique', 'macaron_vtc',
  'photo_interieur_1', 'photo_interieur_2', 'photo_exterieur_1', 'photo_exterieur_2',
];

export default function PartnerVehiclesTab() {
  const [vehicles, setVehicles] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ license_plate: '', brand: '', model: '', color: '', year: new Date().getFullYear(), capacity: 3 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [assigning, setAssigning] = useState(null);

  const fetchVehicles = async () => {
    try {
      const res = await axios.get(`${API}/partner/vehicles/list`, { headers: getHeaders() });
      setVehicles(res.data?.vehicles || []);
      setDrivers(res.data?.drivers || []);
    } catch {} finally { setLoading(false); }
  };
  useEffect(() => { fetchVehicles(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/partner/vehicles/${editingId}`, form, { headers: getHeaders() });
      } else {
        await axios.post(`${API}/partner/vehicles`, form, { headers: getHeaders() });
      }
      setShowForm(false);
      setEditingId(null);
      fetchVehicles();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce véhicule ?')) return;
    try {
      await axios.delete(`${API}/partner/vehicles/${id}`, { headers: getHeaders() });
      fetchVehicles();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur'); }
  };

  const handleDocUpload = async (vehicleId, docType, file) => {
    if (!file) return;
    setUploading(`${vehicleId}-${docType}`);
    try {
      const fd = new FormData();
      fd.append('document_type', docType);
      fd.append('file', file);
      await axios.post(`${API}/partner/vehicles/${vehicleId}/documents`, fd, {
        headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      fetchVehicles();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur upload'); }
    setUploading(null);
  };

  const handleAssign = async (vehicleId, driverId) => {
    setAssigning(vehicleId);
    try {
      if (driverId) {
        await axios.post(`${API}/partner/vehicles/${vehicleId}/assign`, { driver_id: driverId }, { headers: getHeaders() });
      } else {
        await axios.post(`${API}/partner/vehicles/${vehicleId}/unassign`, {}, { headers: getHeaders() });
      }
      fetchVehicles();
    } catch (err) { alert(err.response?.data?.detail || 'Erreur attribution'); }
    setAssigning(null);
  };

  const openEdit = (v) => {
    setForm({ license_plate: v.license_plate, brand: v.brand, model: v.model, color: v.color || '', year: v.year || new Date().getFullYear(), capacity: v.capacity || 3 });
    setEditingId(v.id);
    setShowForm(true);
  };

  const openCreate = () => {
    setForm({ license_plate: '', brand: '', model: '', color: '', year: new Date().getFullYear(), capacity: 3 });
    setEditingId(null);
    setShowForm(true);
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 text-[#C9A24D] animate-spin" /></div>;

  return (
    <div className="space-y-4" data-testid="vehicles-tab">
      <div className="flex items-center justify-between">
        <h2 className="text-white font-semibold flex items-center gap-2">
          <Car className="w-5 h-5 text-[#C9A24D]" /> Véhicules ({vehicles.length})
        </h2>
        <Button onClick={openCreate} className="bg-[#C9A24D] hover:bg-[#b8913f] text-black" data-testid="add-vehicle-btn">
          <Plus className="w-4 h-4 mr-1" /> Ajouter
        </Button>
      </div>

      {/* Vehicle Form Modal */}
      {showForm && (
        <Card className="bg-[#1F1F1F] border-[#C9A24D]/30">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-white text-sm font-medium">{editingId ? 'Modifier' : 'Nouveau'} véhicule</h3>
              <button onClick={() => setShowForm(false)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {[
                { key: 'license_plate', label: 'Immatriculation *', placeholder: 'AB-123-CD' },
                { key: 'brand', label: 'Marque *', placeholder: 'Mercedes' },
                { key: 'model', label: 'Modèle *', placeholder: 'Classe V' },
                { key: 'color', label: 'Couleur', placeholder: 'Noir' },
                { key: 'year', label: 'Année', type: 'number' },
                { key: 'capacity', label: 'Capacité (pax)', type: 'number' },
              ].map(f => (
                <div key={f.key}>
                  <Label className="text-white/50 text-[10px] mb-1 block">{f.label}</Label>
                  <Input
                    type={f.type || 'text'}
                    value={form[f.key]}
                    onChange={e => setForm({ ...form, [f.key]: f.type === 'number' ? parseInt(e.target.value) || 0 : e.target.value })}
                    placeholder={f.placeholder}
                    className="bg-[#0B0B0B] border-[#C9A24D]/20 text-white h-8 text-xs"
                    data-testid={`vehicle-form-${f.key}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <Button variant="outline" onClick={() => setShowForm(false)} className="border-[#C9A24D]/20 text-white/60 text-xs h-8">Annuler</Button>
              <Button onClick={handleSave} disabled={saving || !form.license_plate || !form.brand || !form.model}
                className="bg-[#C9A24D] hover:bg-[#b8913f] text-black text-xs h-8" data-testid="save-vehicle-btn">
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : editingId ? 'Modifier' : 'Soumettre'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Vehicle List */}
      {vehicles.length === 0 ? (
        <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
          <CardContent className="p-8 text-center">
            <Car className="w-10 h-10 text-[#C9A24D]/20 mx-auto mb-3" />
            <p className="text-white/40 text-sm">Aucun véhicule. Cliquez sur "Ajouter" pour soumettre un véhicule.</p>
          </CardContent>
        </Card>
      ) : (
        vehicles.map(v => {
          const st = STATUS_BADGE[v.vehicle_status] || STATUS_BADGE.PENDING_VERIFICATION;
          const vDocs = v.documents || [];
          return (
            <Card key={v.id} className="bg-[#1F1F1F] border-[#C9A24D]/10" data-testid={`vehicle-card-${v.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-[#C9A24D] font-mono font-bold text-sm">{v.license_plate}</span>
                      <span className={`text-[10px] px-2 py-0.5 rounded font-medium ${st.cls}`}>{st.label}</span>
                    </div>
                    <p className="text-white text-sm mt-0.5">{v.brand} {v.model} {v.color && `· ${v.color}`} {v.year && `· ${v.year}`}</p>
                    <p className="text-white/40 text-xs">{v.capacity} passagers</p>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(v)} className="p-1.5 text-white/40 hover:text-[#C9A24D] rounded hover:bg-[#C9A24D]/10"><Edit2 className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(v.id)} className="p-1.5 text-white/40 hover:text-red-400 rounded hover:bg-red-500/10"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>

                {/* Driver Assignment */}
                <div className="border-t border-[#C9A24D]/10 pt-2 mt-2">
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Chauffeur attribué</p>
                  <div className="flex items-center gap-2">
                    {assigning === v.id ? (
                      <Loader2 className="w-4 h-4 text-[#C9A24D] animate-spin" />
                    ) : (
                      <>
                        <select
                          value={v.assigned_driver_id || ''}
                          onChange={e => handleAssign(v.id, e.target.value)}
                          className="flex-1 h-8 px-2 bg-[#0B0B0B] border border-white/10 text-white text-xs rounded focus:border-[#C9A24D]/50 outline-none"
                          data-testid={`assign-driver-${v.id}`}
                        >
                          <option value="">— Aucun —</option>
                          {drivers.map(d => (
                            <option key={d.id} value={d.id}>{d.name} ({d.email})</option>
                          ))}
                        </select>
                        {v.assigned_driver_id && (
                          <button onClick={() => handleAssign(v.id, '')}
                            className="p-1.5 text-red-400/60 hover:text-red-400 rounded hover:bg-red-500/10"
                            title="Retirer l'attribution" data-testid={`unassign-${v.id}`}>
                            <UserX className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  {v.assigned_driver_name && !assigning && (
                    <p className="text-xs text-blue-400 mt-1 flex items-center gap-1">
                      <User className="w-3 h-3" /> {v.assigned_driver_name}
                    </p>
                  )}
                </div>

                {/* Vehicle Documents */}
                <div className="border-t border-[#C9A24D]/10 pt-2 mt-2">
                  <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Documents véhicule</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                    {VDOC_TYPES.map(dt => {
                      const doc = vDocs.find(d => d.document_type === dt);
                      return (
                        <div key={dt} className="p-2 bg-[#0B0B0B] rounded text-center" data-testid={`vdoc-${v.id}-${dt}`}>
                          {doc ? (
                            <div className="flex items-center gap-1 justify-center mb-1">
                              {doc.status === 'PENDING' ? <Clock className="w-3 h-3 text-amber-400" /> : <CheckCircle className="w-3 h-3 text-green-400" />}
                              <span className="text-[10px] text-white/60">{doc.status === 'PENDING' ? 'En attente' : 'OK'}</span>
                            </div>
                          ) : (
                            <FileText className="w-4 h-4 text-white/20 mx-auto mb-1" />
                          )}
                          <p className="text-[9px] text-white/40 mb-1">{VDOC_LABELS[dt]}</p>
                          <label className="cursor-pointer">
                            <input type="file" accept=".pdf,.jpg,.jpeg,.png" className="hidden"
                              onChange={e => handleDocUpload(v.id, dt, e.target.files[0])} />
                            <span className="text-[9px] text-[#C9A24D] hover:underline flex items-center justify-center gap-0.5">
                              {uploading === `${v.id}-${dt}` ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Upload className="w-2.5 h-2.5" />}
                              {doc ? 'Remplacer' : 'Uploader'}
                            </span>
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })
      )}
    </div>
  );
}
